
import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileBottomNav } from "./MobileBottomNav";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toaster";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { cn } from "@/lib/utils";

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { preferences } = useUserPreferences();
  
  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  return (
    <div className={cn("flex min-h-screen bg-background/80", preferences.compact_mode && "compact")}>
      {preferences.reduced_motion && (
        <style>{`*, *::before, *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; scroll-behavior: auto !important; }`}</style>
      )}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          sidebarOpen 
            ? 'translate-x-0 opacity-100' 
            : '-translate-x-full opacity-0 md:translate-x-0 md:opacity-100'
        } fixed md:sticky md:top-0 left-0 bottom-0 z-40 md:z-30 h-screen`}
      >
        <Sidebar />
      </div>
      
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-background/20 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="flex-1 flex flex-col min-h-screen max-w-full">
        <Header>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden hover:bg-muted/80 text-muted-foreground" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle sidebar</span>
          </Button>
        </Header>
        
        <main className={`flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden overflow-y-auto ${isMobile ? 'pb-20' : ''}`}>
          <div className="container mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
        
        <footer className="py-3 px-6 text-center border-t border-border bg-muted/30 text-xs text-muted-foreground">
          <div className="container mx-auto max-w-7xl flex flex-col sm:flex-row justify-between items-center gap-2">
            <span>© 2026 mySuccession. All rights reserved.</span>
            <div className="flex gap-4">
              <a href="/terms" className="hover:text-primary transition-colors animated-underline">Terms of Service</a>
              <a href="/privacy" className="hover:text-primary transition-colors animated-underline">Privacy Policy</a>
              <a href="mailto:support@mysuccession.io" className="hover:text-primary transition-colors animated-underline">Support</a>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
      
      {/* Global toaster for notifications */}
      <Toaster />
    </div>
  );
}
